import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Receipt, ChartBar, CalendarBlank, Pencil, Printer, FilePdf, WhatsappLogo, Trash, MagnifyingGlass, FileText } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function BilledQuotations() {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [quotations, setQuotations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => { fetchBilled(); }, []);
  useEffect(() => {
    if (!searchQuery.trim()) { setFiltered(quotations); return; }
    const q = searchQuery.toLowerCase();
    setFiltered(quotations.filter(qt =>
      (qt.voucher_number || '').toLowerCase().includes(q) ||
      (qt.buyer_name || '').toLowerCase().includes(q) ||
      (qt.buyer_mobile || '').toLowerCase().includes(q)
    ));
  }, [searchQuery, quotations]);

  const fetchBilled = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quotations-billed`);
      setQuotations(data);
    } catch (e) { toast.error('Failed to load billed quotations'); }
  };

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quotations-billed/analytics?month=${selectedMonth}&year=${selectedYear}`);
      setAnalytics(data);
      setShowAnalytics(true);
    } catch (e) { toast.error('Failed to load analytics'); }
  };

  const moveToNonBilled = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/quotations/${id}/mark-unbilled`);
      toast.success('Moved back to Non-Billed Quotations');
      fetchBilled();
      if (showAnalytics) fetchAnalytics();
    } catch (e) { toast.error('Failed'); }
  };

  const editQuotation = (quotation) => {
    navigate('/create-quotation', { state: { editQuotation: quotation } });
  };

  const printQuotation = (quotation) => {
    const token = localStorage.getItem('access_token');
    window.location.href = `${BACKEND_URL}/api/quotations/${quotation.id}/pdf?token=${token}`;
    toast.info('PDF downloading — open it to print');
  };

  const downloadPDF = (id, voucherNumber) => {
    const token = localStorage.getItem('access_token');
    window.location.href = `${BACKEND_URL}/api/quotations/${id}/pdf?token=${token}`;
  };

  const shareWhatsApp = async (q) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotations/${q.id}/pdf`, { responseType: 'blob' });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfFile = new File([pdfBlob], `Quotation_${q.voucher_number}.pdf`, { type: 'application/pdf' });
      let shared = false;
      try {
        if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
          await navigator.share({ files: [pdfFile], title: `Quotation ${q.voucher_number}`, text: `Quotation ${q.voucher_number} - Total: Rs.${q.grand_total?.toFixed(2)}` });
          shared = true;
        }
      } catch (e) { /* fallback */ }
      if (!shared) {
        const token = localStorage.getItem('access_token');
        window.location.href = `${BACKEND_URL}/api/quotations/${q.id}/pdf?token=${token}`;
        const phone = q.buyer_mobile ? q.buyer_mobile.replace(/\D/g, '') : '';
        const fullPhone = phone.length === 10 ? `91${phone}` : phone;
        const msg = encodeURIComponent(`Quotation ${q.voucher_number}\nTotal: Rs.${q.grand_total?.toFixed(2)}\nPlease find the PDF attached.`);
        const waUrl = fullPhone ? `https://wa.me/${fullPhone}?text=${msg}` : `https://wa.me/?text=${msg}`;
        setTimeout(() => { window.location.href = waUrl; }, 1500);
        toast.info('PDF downloading! WhatsApp will open shortly.');
      }
    } catch (e) { toast.error('Error sharing'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <header className="bg-white border-b border-border shadow-sm">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="billed-title">
              <Receipt size={22} weight="bold" className="text-green-600" /> Ease Quote
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-[#0066FF] hover:bg-[#0052CC] text-white" onClick={() => navigate('/create-quotation')} data-testid="create-quotation-nav">
              <FileText size={14} className="mr-1" /> Create Quotation
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/quotations')} data-testid="go-to-nonbilled">
              Non-Billed
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/products')} data-testid="products-nav">
              Products
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/catalog')} data-testid="catalog-nav">
              Catalog
            </Button>
          </div>
        </div>
      </header>

      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-4">
        {/* Analytics Controls */}
        <div className="bg-white border border-border rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CalendarBlank size={16} className="text-gray-500" />
              <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} className="h-8 border border-border rounded px-2 text-sm" data-testid="analytics-month">
                {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="h-8 border border-border rounded px-2 text-sm" data-testid="analytics-year">
                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <Button onClick={fetchAnalytics} size="sm" className="h-8 bg-green-600 hover:bg-green-700 text-white" data-testid="show-analytics-btn">
              <ChartBar size={14} className="mr-1" /> Show Analytics
            </Button>
          </div>

          {showAnalytics && analytics && (
            <div className="mt-4 border-t border-border pt-4" data-testid="analytics-panel">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="text-[10px] text-green-700 uppercase font-semibold">Monthly Total</div>
                  <div className="text-xl font-bold text-green-800 mt-1">{analytics.total_value.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-[10px] text-blue-700 uppercase font-semibold">Billed Quotations</div>
                  <div className="text-xl font-bold text-blue-800 mt-1">{analytics.total_count}</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="text-[10px] text-purple-700 uppercase font-semibold">Period</div>
                  <div className="text-xl font-bold text-purple-800 mt-1">{months[analytics.month - 1]} {analytics.year}</div>
                </div>
              </div>
              {analytics.brand_breakdown.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase mb-2">Brand-wise Sales Breakdown</h3>
                  <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#111827]">
                          <TableHead className="text-white text-[11px] font-medium">Brand</TableHead>
                          <TableHead className="text-white text-[11px] font-medium">Category</TableHead>
                          <TableHead className="text-white text-[11px] font-medium text-right">Qty</TableHead>
                          <TableHead className="text-white text-[11px] font-medium text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analytics.brand_breakdown.map((row, i) => (
                          <TableRow key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <TableCell className="text-xs font-medium">{row.brand}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{row.category}</TableCell>
                            <TableCell className="text-xs text-right">{row.qty}</TableCell>
                            <TableCell className="text-xs font-semibold text-right">{row.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              {analytics.brand_breakdown.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No billed quotations for this period</p>
              )}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 bg-white border border-border rounded-lg shadow-sm px-3 py-2">
          <MagnifyingGlass size={16} className="text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by party name, mobile, or quotation number..."
            className="flex-1 text-sm outline-none bg-transparent"
            data-testid="search-billed-input"
          />
        </div>

        {/* Billed Quotations Table */}
        <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <span className="text-sm text-muted-foreground">{filtered.length} billed quotation(s)</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#111827]">
                  <TableHead className="text-white text-xs font-medium">Voucher No.</TableHead>
                  <TableHead className="text-white text-xs font-medium">Buyer Name</TableHead>
                  <TableHead className="text-white text-xs font-medium">Mobile</TableHead>
                  <TableHead className="text-white text-xs font-medium">Items</TableHead>
                  <TableHead className="text-white text-xs font-medium">Grand Total</TableHead>
                  <TableHead className="text-white text-xs font-medium">Billed Date</TableHead>
                  <TableHead className="text-white text-xs font-medium w-[180px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">No billed quotations found</TableCell></TableRow>
                ) : filtered.map((q, idx) => (
                  <TableRow key={q.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <TableCell className="text-xs font-mono font-semibold text-green-700">{q.voucher_number}</TableCell>
                    <TableCell className="text-xs font-medium">{q.buyer_name || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{q.buyer_mobile || '-'}</TableCell>
                    <TableCell className="text-xs">{q.items?.length || 0}</TableCell>
                    <TableCell className="text-xs font-bold text-green-700">{q.grand_total?.toFixed(2)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(q.billed_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button onClick={() => editQuotation(q)} className="text-muted-foreground hover:text-foreground p-1" title="Edit" data-testid={`edit-billed-${idx}`}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => printQuotation(q)} className="text-muted-foreground hover:text-foreground p-1" title="Print" data-testid={`print-billed-${idx}`}>
                          <Printer size={14} />
                        </button>
                        <button onClick={() => downloadPDF(q.id, q.voucher_number)} className="text-muted-foreground hover:text-foreground p-1" title="Download PDF" data-testid={`pdf-billed-${idx}`}>
                          <FilePdf size={14} />
                        </button>
                        <button onClick={() => shareWhatsApp(q)} className="text-[#25D366] hover:text-[#1DA851] p-1" title="WhatsApp" data-testid={`whatsapp-billed-${idx}`}>
                          <WhatsappLogo size={14} weight="fill" />
                        </button>
                        <button onClick={() => moveToNonBilled(q.id)} className="text-red-400 hover:text-red-600 p-1" title="Remove from Billed (move to Non-Billed)" data-testid={`delete-billed-${idx}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
